"use client";

import styles from "./ProfileTabsSkeleton.module.css";

/**
 * Skeleton dla sidebar nav (3 core items) — pokazywany gdy profile się ładuje.
 * Header (avatar/name/email) zachowuje rzeczywisty render z `ProfileSidebarHeader`
 * (działa z userSession fallback) — skeleton tylko dla nav items.
 *
 * Layout: 3 row'y z ikoną (circle) + label (rect), w collapsed mode tylko ikony.
 */
export function ProfileSidebarNavSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <nav className={styles.navSkeleton} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className={collapsed ? styles.navItemCollapsed : styles.navItem}>
          <span className={`skeleton-base ${styles.navIcon}`} />
          {!collapsed && <span className={`skeleton-base ${styles.navLabel}`} />}
        </div>
      ))}
    </nav>
  );
}

/**
 * Skeleton dla osobnego plugin boksa w sidebar column (pod profile aside).
 * Renderowany w trakcie ładowania extensions (przed pierwszym fetch'em).
 *
 * Layout: header (title rect + badge circle) + 4 row items z ikoną + label.
 */
export function PluginSidebarBoxSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <section className={collapsed ? styles.pluginBoxCollapsed : styles.pluginBox} aria-hidden="true">
      {!collapsed && (
        <div className={styles.pluginBoxHeader}>
          <span className={`skeleton-base ${styles.pluginBoxTitle}`} />
        </div>
      )}
      <div className={styles.pluginBoxList}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={collapsed ? styles.navItemCollapsed : styles.navItem}>
            <span className={`skeleton-base ${styles.navIcon}`} />
            {!collapsed && <span className={`skeleton-base ${styles.navLabel}`} />}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Skeleton dla main content area (Twoje dane / Zmiana hasła / Konto / plugin view).
 * Layout: TabHeader skeleton (icon + title) + 4-6 form fields (label + input).
 *
 * Używany w ProfileTabs gdy `loading=true` zamiast pustego placeholder text.
 */
export function ProfileMainSkeleton() {
  return (
    <div className={styles.mainSkeleton} aria-hidden="true">
      {/* TabHeader skeleton */}
      <header className={styles.headerSkeleton}>
        <span className={`skeleton-base ${styles.headerIcon}`} />
        <div className={styles.headerTitles}>
          <span className={`skeleton-base ${styles.headerTitleShort}`} />
          <span className={`skeleton-base ${styles.headerTitle}`} />
        </div>
      </header>

      {/* Sub-tabs skeleton (Personal/Company/Billing) */}
      <div className={styles.subTabs}>
        {[0, 1, 2].map((i) => (
          <span key={i} className={`skeleton-base ${styles.subTab}`} />
        ))}
      </div>

      {/* Form fields skeleton — 5 par "label + input". */}
      <div className={styles.fields}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.field}>
            <span className={`skeleton-base ${styles.fieldLabel}`} />
            <span className={`skeleton-base ${styles.fieldInput}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
